/**
 * Die unendliche Flaeche einer Bibel-Notiz.
 *
 * Aufbau
 *   .board          fester Ausschnitt (der Bildschirm), schneidet ab
 *     .board__world  die Welt, per CSS-transform verschoben und gezoomt
 *       svg          die Verbindungslinien der Mindmap
 *       .obj         die Objekte (Text, Karte, Knoten, Liste, Form, Bild, Ton)
 *
 * Raster
 *   Das Hintergrundraster ist ein CSS-Hintergrund auf .board, kein Objekt.
 *   Damit kann es nie ausgewaehlt, verschoben oder geloescht werden, kostet
 *   nichts an Rechenzeit und skaliert beim Zoomen einfach mit.
 *
 * Koordinaten
 *   Objekte stehen in Weltkoordinaten (Pixel bei 100 Prozent). Verschieben
 *   und Zoomen aendert nur die transform der Welt, nie die Daten.
 *
 * Handschrift
 *   Es gibt hier KEINE eigene Zeichenlogik. Die Flaeche meldet dem Rahmen
 *   host = .board und content = .board__world, und die vorhandene
 *   Zeichenebene (features/drawing/surface.ts) macht den Rest. Weil sie ihre
 *   Striche als Anteil der Inhaltsbreite speichert und die Einheit von uns
 *   bekommt (= der Zoomfaktor), bleibt Handschrift beim Zoomen und
 *   Verschieben genau dort, wo sie hingehoert – ohne doppelten Code.
 *
 * Eingaben
 *   Stift zeichnet (sobald ein Werkzeug an ist, faengt die Zeichenebene ihn
 *   vorher ab), Finger verschiebt und bedient, zwei Finger zoomen.
 */
import type {
  Board,
  CanvasBackground,
  CanvasObject,
  CanvasEdge,
} from '../../types.ts';
import { WORLD_WIDTH, defaultFontSize, emptyObject } from '../../db/repo.ts';
import { getActiveSurface } from '../drawing/surface.ts';
import { drawsWith } from '../drawing/tools.ts';
import { h } from '../../ui/dom.ts';
import { formatTime, paintProgress, seekRatio, waveformSvg } from './audio.ts';

const MIN_SCALE = 0.25;
const MAX_SCALE = 3;
/** Rand, ab dem die Welt nach unten weiterwaechst. */
const GROW_MARGIN = 900;
const GROW_STEP = 2000;
/** Objekte ausserhalb dieses Randes werden nicht gezeichnet. */
const CULL_MARGIN = 400;

/** Abstand der Rasterlinien in Weltpixeln. */
const RASTER_STEP = 32;

/** Kleinste Groesse eines Objekts in Weltpixeln. */
const MIN_W = 56;
const MIN_H = 36;

/**
 * Die acht Griffe am gewaehlten Objekt.
 * Ecken aendern beide Seiten, Kanten nur eine – wie man es von jedem
 * Zeichenprogramm kennt. "n"/"w" heisst: die Kante oben bzw. links wandert,
 * das Objekt waechst also nach oben oder links.
 */
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type Handle = (typeof HANDLES)[number];

/** Arten, die beim Ziehen an der Ecke ihr Seitenverhaeltnis behalten. */
const KEEP_RATIO = new Set<CanvasObject['type']>(['image', 'audio']);

export interface BoardCanvasOptions {
  board: Board;
  /** Wird nach jeder Aenderung gerufen; das Speichern entprellt der Aufrufer. */
  onChange: (board: Board) => void;
  /** Auswahl hat sich geaendert – die Werkzeugleiste richtet sich danach. */
  onSelect?: (object: CanvasObject | null) => void;
  /** Eine Verbindung wurde gewaehlt (oder abgewaehlt). */
  onEdgeSelect?: (edge: CanvasEdge | null) => void;
}

export interface BoardCanvas {
  element: HTMLElement;
  /** Angaben fuer die gemeinsame Zeichenebene. */
  drawing: { host: HTMLElement; content: HTMLElement; getUnit: () => number };
  /** Legt ein Objekt in der Mitte des sichtbaren Bereichs an. */
  addObject(partial: Partial<CanvasObject>): CanvasObject;
  /** Das gerade gewaehlte Objekt. */
  selected(): CanvasObject | null;
  /** Die gerade gewaehlte Verbindung. */
  selectedEdge(): CanvasEdge | null;
  /** Loescht, was gerade gewaehlt ist – Objekt oder Verbindung. */
  deleteSelected(): void;
  /**
   * Uebernimmt Aenderungen am gewaehlten Objekt (Farbe, Schrift, Form) und
   * zeichnet nur dieses eine Objekt neu.
   */
  updateSelected(patch: Partial<CanvasObject>): void;
  /** Aendert das Hintergrundraster der Flaeche. */
  setBackground(kind: CanvasBackground): void;
  background(): CanvasBackground;
  /** Verbindungsmodus: das naechste angetippte Objekt wird verbunden. */
  startLink(): void;
  linking(): boolean;
  cancelLink(): void;
  zoomBy(factor: number): void;
  /** Alles ins Bild holen. */
  fit(): void;
  destroy(): void;
}

export function createBoardCanvas(opts: BoardCanvasOptions): BoardCanvas {
  const board: Board = {
    ...opts.board,
    objects: opts.board.objects.map((o) => ({ ...o })),
    edges: opts.board.edges.map((e) => ({ ...e })),
  };

  let scale = clampScale(board.view.scale || 1);
  // Verschiebung in Bildschirmpixeln: Weltpunkt (0,0) liegt hier.
  let panX = -board.view.x * scale;
  let panY = -board.view.y * scale;

  let selectedId: string | null = null;
  let selectedEdgeId: string | null = null;
  let linkFrom: string | null = null;

  /* ------------------------------------------------------------ Aufbau */

  const edgeLayer = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  edgeLayer.setAttribute('class', 'board__edges');

  const world = h('div', { class: 'board__world' });
  world.append(edgeLayer);

  const element = h('div', { class: 'board' }, world);

  /** Element je Objekt, damit Bewegen kein Neuaufbau ist. */
  const nodes = new Map<string, HTMLElement>();
  /** Die <audio>-Elemente der Ton-Bausteine, zum Anhalten beim Verlassen. */
  const audios = new Map<string, HTMLAudioElement>();

  /* ------------------------------------------------------- Hilfsmittel */

  function clampScale(value: number): number {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
  }

  function find(id: string | null): CanvasObject | null {
    if (!id) return null;
    return board.objects.find((o) => o.id === id) ?? null;
  }

  /** Bildschirmpunkt in Weltkoordinaten. */
  function toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = element.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panX) / scale,
      y: (clientY - rect.top - panY) / scale,
    };
  }

  let changeTimer = 0;
  function changed(): void {
    // Sammelt schnelle Folgen (z. B. beim Ziehen) zu einem Aufruf.
    clearTimeout(changeTimer);
    changeTimer = window.setTimeout(() => {
      board.view = { x: -panX / scale, y: -panY / scale, scale };
      opts.onChange({
        ...board,
        objects: board.objects.map((o) => ({ ...o })),
        edges: board.edges.map((e) => ({ ...e })),
      });
    }, 250);
  }

  /* ----------------------------------------------------- Welt zeichnen */

  function applyTransform(): void {
    world.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
    // Das Raster im Hintergrund wandert und waechst mit, ohne dass dafuer
    // ein riesiges Element gezeichnet werden muss.
    const step = RASTER_STEP * scale;
    element.style.backgroundSize =
      board.background === 'lines' ? `100% ${step}px` : `${step}px ${step}px`;
    element.style.backgroundPosition = `${panX}px ${panY}px`;
    cull();
    // Die Handschrift liegt auf einer eigenen Leinwand und muss nach jeder
    // Bewegung neu gemessen werden – sonst bliebe sie stehen.
    getActiveSurface()?.refresh();
  }

  /** Traegt die gewaehlte Rasterart als data-Attribut ein; den Rest macht CSS. */
  function applyBackground(): void {
    element.dataset.raster = board.background ?? 'dots';
    applyTransform();
  }

  function applySize(): void {
    world.style.width = `${WORLD_WIDTH}px`;
    world.style.height = `${board.worldHeight}px`;
    edgeLayer.setAttribute('width', String(WORLD_WIDTH));
    edgeLayer.setAttribute('height', String(board.worldHeight));
    edgeLayer.setAttribute('viewBox', `0 0 ${WORLD_WIDTH} ${board.worldHeight}`);
  }

  /** Laesst die Welt nach unten weiterwachsen, damit sie nie „zu Ende“ ist. */
  function growIfNeeded(): void {
    let needed = board.worldHeight;
    for (const o of board.objects) needed = Math.max(needed, o.y + o.h + GROW_MARGIN);
    const visibleBottom = (-panY + element.clientHeight) / scale + GROW_MARGIN;
    needed = Math.max(needed, visibleBottom);
    if (needed <= board.worldHeight) return;
    board.worldHeight = Math.ceil(needed / GROW_STEP) * GROW_STEP;
    applySize();
  }

  /**
   * Nur zeichnen, was man sehen kann.
   * Bei vielen Objekten spart das jede Menge Layout-Arbeit.
   */
  function cull(): void {
    const left = -panX / scale - CULL_MARGIN;
    const top = -panY / scale - CULL_MARGIN;
    const right = left + element.clientWidth / scale + CULL_MARGIN * 2;
    const bottom = top + element.clientHeight / scale + CULL_MARGIN * 2;
    for (const object of board.objects) {
      const node = nodes.get(object.id);
      if (!node) continue;
      const outside =
        object.x > right || object.x + object.w < left || object.y > bottom || object.y + object.h < top;
      // Das gewaehlte Objekt bleibt sichtbar, sonst verschwinden die Griffe.
      node.hidden = outside && object.id !== selectedId;
    }
  }

  /* ---------------------------------------------------------- Objekte */

  function objectNode(object: CanvasObject): HTMLElement {
    const grip = h('div', {
      class: 'obj__grip',
      attrs: { title: 'Verschieben', 'aria-label': 'Verschieben' },
    });

    const body = h('div', { class: 'obj__body' });

    const node = h(
      'div',
      { class: `obj obj--${object.type}`, data: { id: object.id } },
      grip,
      body,
    );

    // Acht Griffe, aber erst sichtbar, wenn das Objekt gewaehlt ist. Sie
    // liegen ausserhalb von .obj__body, damit sie nie unter dem Text landen.
    for (const handle of HANDLES) {
      const el = h('div', {
        class: `obj__handle obj__handle--${handle}`,
        data: { handle },
        attrs: { 'aria-label': 'Groesse aendern' },
      });
      el.addEventListener('pointerdown', (ev) => startDrag(ev, object, handle));
      node.append(el);
    }

    grip.addEventListener('pointerdown', (ev) => startDrag(ev, object, 'move'));

    node.addEventListener('pointerdown', (ev) => {
      // Beim Zeichnen faengt die Zeichenebene den Stift schon vorher ab.
      if (drawsWith(ev)) return;
      ev.stopPropagation();
      if (linkFrom) {
        completeLink(object.id);
        return;
      }
      select(object.id);
    });

    paintBody(object, body);
    placeNode(object, node);
    return node;
  }

  /** Inhalt eines Objekts: Text, Liste, Form, Bild oder Ton. */
  function paintBody(object: CanvasObject, body: HTMLElement): void {
    body.textContent = '';

    if (object.type === 'image') {
      const img = h('img', { class: 'obj__image', attrs: { alt: object.name || 'Bild' } });
      if (object.src) img.src = object.src;
      body.append(img);
      return;
    }

    if (object.type === 'audio') {
      body.append(audioPlayer(object));
      return;
    }

    if (object.type === 'shape') {
      // Die Form liegt als SVG darunter, die Beschriftung darueber. So
      // laesst sich auch ein Kreis beschriften, ohne den Text zu verbiegen.
      body.append(shapeArt(object), textField(object, true));
      return;
    }

    if (object.type === 'checklist') {
      body.append(checklist(object));
      return;
    }

    body.append(textField(object, false));
  }

  /** Das Schreibfeld eines Objekts, samt Schriftgestaltung. */
  function textField(object: CanvasObject, overlay: boolean): HTMLElement {
    const area = h('textarea', {
      class: 'obj__text' + (overlay ? ' obj__text--overlay' : ''),
      attrs: {
        placeholder: object.type === 'node' ? 'Begriff' : object.type === 'shape' ? '' : 'Text …',
        // Mit dem Pencil kann man per Scribble hineinschreiben, solange
        // kein Werkzeug aktiv ist.
        autocapitalize: 'sentences',
        autocorrect: 'off',
        spellcheck: 'false',
      },
    });
    area.value = object.text;
    area.addEventListener('input', () => {
      object.text = area.value;
      changed();
    });
    area.addEventListener('focus', () => select(object.id));
    applyTextStyle(object, area);
    return area;
  }

  /** Schriftgroesse, Fett, Kursiv und Farbe auf ein Feld anwenden. */
  function applyTextStyle(object: CanvasObject, el: HTMLElement): void {
    el.style.fontSize = `${object.fontSize ?? defaultFontSize(object.type)}px`;
    el.style.fontWeight = object.bold ? '700' : '';
    el.style.fontStyle = object.italic ? 'italic' : '';
  }

  /** Rechteck oder Ellipse als SVG – skaliert sauber mit dem Objekt mit. */
  function shapeArt(object: CanvasObject): SVGSVGElement {
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'obj__shape');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');

    const kind = object.shape === 'ellipse' ? 'ellipse' : 'rect';
    const el = document.createElementNS(NS, kind);
    if (kind === 'ellipse') {
      el.setAttribute('cx', '50');
      el.setAttribute('cy', '50');
      el.setAttribute('rx', '49');
      el.setAttribute('ry', '49');
    } else {
      el.setAttribute('x', '1');
      el.setAttribute('y', '1');
      el.setAttribute('width', '98');
      el.setAttribute('height', '98');
      el.setAttribute('rx', '3');
    }
    el.setAttribute('class', 'obj__shape-art');
    // Ohne vectorEffect wuerde die Linie beim Strecken mitverzerren.
    el.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.append(el);
    return svg;
  }

  /**
   * Der Ton-Baustein: Name, Start/Pause, Wellenform, Laufzeit.
   * Abgespielt wird mit einem <audio>-Element, das die Data-URL direkt
   * versteht – dazu braucht es weder Netz noch Server.
   */
  function audioPlayer(object: CanvasObject): HTMLElement {
    // Ein frueher gebauter Spieler zum selben Baustein darf nicht
    // weiterlaufen, wenn der Inhalt neu gezeichnet wird.
    audios.get(object.id)?.pause();

    const el = new Audio();
    el.preload = 'metadata';
    if (object.src) el.src = object.src;
    audios.set(object.id, el);

    const wave = waveformSvg(object.peaks ?? []);
    const time = h('span', { class: 'obj-audio__time', text: formatTime(object.duration ?? 0) });

    const play = h('button', {
      class: 'obj-audio__play',
      text: '▶',
      attrs: { type: 'button', 'aria-label': 'Abspielen' },
    });

    function paintButton(): void {
      const playing = !el.paused && !el.ended;
      play.textContent = playing ? '❙❙' : '▶';
      play.setAttribute('aria-label', playing ? 'Pause' : 'Abspielen');
      play.classList.toggle('obj-audio__play--on', playing);
    }

    play.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (el.paused) {
        // Immer nur eine Aufnahme gleichzeitig – sonst spielen zwei Demos
        // uebereinander, und niemand hoert etwas Sinnvolles.
        for (const [id, other] of audios) if (id !== object.id) other.pause();
        void el.play().catch(() => undefined);
      } else {
        el.pause();
      }
      paintButton();
    });

    el.addEventListener('timeupdate', () => {
      if (el.duration > 0) paintProgress(wave, el.currentTime / el.duration);
      time.textContent = formatTime(el.duration - el.currentTime);
    });
    el.addEventListener('play', paintButton);
    el.addEventListener('pause', paintButton);
    el.addEventListener('ended', () => {
      paintProgress(wave, 0);
      time.textContent = formatTime(object.duration ?? el.duration);
      paintButton();
    });
    el.addEventListener('loadedmetadata', () => {
      // Erst jetzt kennt der Browser die Laenge sicher.
      if (!object.duration && Number.isFinite(el.duration)) {
        object.duration = el.duration;
        changed();
      }
      time.textContent = formatTime(object.duration ?? el.duration);
    });

    // In die Wellenform tippen springt an die Stelle.
    wave.addEventListener('pointerdown', (ev) => {
      ev.stopPropagation();
      if (el.duration > 0) el.currentTime = seekRatio(wave, ev.clientX) * el.duration;
    });

    return h(
      'div',
      { class: 'obj-audio' },
      h(
        'div',
        { class: 'obj-audio__head' },
        h('span', { class: 'obj-audio__name', text: object.name || object.text || 'Aufnahme' }),
        time,
      ),
      h('div', { class: 'obj-audio__row' }, play, wave),
    );
  }

  /** Eine Zeile je Punkt, "[x] " am Anfang bedeutet erledigt. */
  function checklist(object: CanvasObject): HTMLElement {
    const list = h('div', { class: 'obj__list' });

    const lines = object.text === '' ? [''] : object.text.split('\n');
    lines.forEach((line, index) => {
      const done = /^\[x\]\s?/i.test(line);
      const label = line.replace(/^\[[ x]\]\s?/i, '');

      const box = h('button', {
        class: 'obj__check' + (done ? ' obj__check--on' : ''),
        text: done ? '✓' : '',
        attrs: { type: 'button', 'aria-pressed': String(done), 'aria-label': 'Erledigt' },
        on: {
          click: () => {
            const next = object.text.split('\n');
            next[index] = done ? label : `[x] ${label}`;
            object.text = next.join('\n');
            const node = nodes.get(object.id);
            if (node) paintBody(object, node.querySelector('.obj__body') as HTMLElement);
            changed();
          },
        },
      });

      const input = h('input', {
        class: 'obj__line' + (done ? ' obj__line--done' : ''),
        attrs: { type: 'text', placeholder: 'Punkt …', autocorrect: 'off', spellcheck: 'false' },
      });
      input.value = label;
      input.addEventListener('input', () => {
        const next = object.text.split('\n');
        next[index] = done ? `[x] ${input.value}` : input.value;
        object.text = next.join('\n');
        changed();
      });
      input.addEventListener('keydown', (ev) => {
        if (ev.key !== 'Enter') return;
        ev.preventDefault();
        const next = object.text.split('\n');
        next.splice(index + 1, 0, '');
        object.text = next.join('\n');
        const node = nodes.get(object.id);
        if (node) {
          paintBody(object, node.querySelector('.obj__body') as HTMLElement);
          const fields = node.querySelectorAll<HTMLInputElement>('.obj__line');
          fields[index + 1]?.focus();
        }
        changed();
      });

      list.append(h('div', { class: 'obj__row' }, box, input));
    });

    return list;
  }

  /** Position, Groesse und Farben eines Objekts ins DOM schreiben. */
  function placeNode(object: CanvasObject, node: HTMLElement): void {
    node.style.left = `${object.x}px`;
    node.style.top = `${object.y}px`;
    node.style.width = `${object.w}px`;
    node.style.height = `${object.h}px`;
    paintColors(object, node);
  }

  /**
   * Die drei Farben eines Objekts als CSS-Variablen.
   * Ist eine nicht gesetzt, faellt das Stylesheet auf das Theme zurueck –
   * darum wird sie hier entfernt statt auf einen festen Wert gesetzt.
   */
  function paintColors(object: CanvasObject, node: HTMLElement): void {
    const set = (name: string, value: string | undefined) => {
      if (value) node.style.setProperty(name, value);
      else node.style.removeProperty(name);
    };
    set('--obj-color', object.color);
    set('--obj-fill', object.fill);
    set('--obj-stroke', object.stroke || object.color);
  }

  function renderObjects(): void {
    for (const node of nodes.values()) node.remove();
    nodes.clear();
    for (const object of board.objects) {
      const node = objectNode(object);
      nodes.set(object.id, node);
      world.append(node);
    }
    markSelection();
    cull();
  }

  /* -------------------------------------------------------- Verbindungen */

  function renderEdges(): void {
    const NS = 'http://www.w3.org/2000/svg';
    edgeLayer.textContent = '';
    for (const edge of board.edges) {
      const a = find(edge.from);
      const b = find(edge.to);
      if (!a || !b) continue;
      const d = edgePath(a, b);

      const group = document.createElementNS(NS, 'g');
      group.setAttribute(
        'class',
        'board__edge-group' + (edge.id === selectedEdgeId ? ' board__edge-group--on' : ''),
      );

      // Eine breite, unsichtbare Linie darunter: eine 2 Pixel schmale Kurve
      // trifft man mit dem Finger sonst nie.
      const hit = document.createElementNS(NS, 'path');
      hit.setAttribute('class', 'board__edge-hit');
      hit.setAttribute('d', d);

      const line = document.createElementNS(NS, 'path');
      line.setAttribute('class', 'board__edge');
      line.setAttribute('d', d);
      if (edge.color) line.setAttribute('stroke', edge.color);

      group.append(hit, line);
      group.addEventListener('pointerdown', (ev) => {
        if (drawsWith(ev as PointerEvent)) return;
        ev.stopPropagation();
        selectEdge(edge.id);
      });
      edgeLayer.append(group);
    }
  }

  /** Waehlt eine Verbindung; Objektauswahl und Kantenauswahl schliessen sich aus. */
  function selectEdge(id: string | null): void {
    selectedEdgeId = id;
    if (id) {
      selectedId = null;
      markSelection();
    }
    renderEdges();
    opts.onSelect?.(null);
    opts.onEdgeSelect?.(id ? (board.edges.find((e) => e.id === id) ?? null) : null);
  }

  /**
   * Weiche Kurve von Rand zu Rand.
   *
   * Bewusst nicht von Mitte zu Mitte: sonst laeuft die Linie unter den
   * Objekten hindurch. Sie waere dort nicht nur unsichtbar, sondern auch
   * nicht antippbar – bei zwei nah beieinander liegenden Bausteinen bliebe
   * gar nichts uebrig, was man treffen koennte.
   */
  function edgePath(a: CanvasObject, b: CanvasObject): string {
    const ac = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
    const bc = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
    const start = borderPoint(a, bc);
    const end = borderPoint(b, ac);
    // Die Kurve zieht waagerecht aus dem Objekt heraus, das wirkt ruhiger
    // als eine Gerade und verheddert sich bei Mindmaps weniger.
    const bow = Math.abs(end.x - start.x) * 0.4;
    return `M ${start.x} ${start.y} C ${start.x + bow} ${start.y}, ${end.x - bow} ${end.y}, ${end.x} ${end.y}`;
  }

  /**
   * Punkt auf dem Rand von `object` in Richtung `toward`.
   *
   * Gerechnet wird am umschliessenden Rechteck – auch fuer den runden
   * Knoten. Der Unterschied betraegt wenige Pixel und faellt neben einer
   * 2 Pixel starken Linie nicht auf.
   */
  function borderPoint(object: CanvasObject, toward: { x: number; y: number }) {
    const cx = object.x + object.w / 2;
    const cy = object.y + object.h / 2;
    const dx = toward.x - cx;
    const dy = toward.y - cy;
    if (dx === 0 && dy === 0) return { x: cx, y: cy };

    // Wie weit kommt man Richtung `toward`, bevor eine Kante erreicht ist?
    const halfW = object.w / 2;
    const halfH = object.h / 2;
    const stepX = dx === 0 ? Number.POSITIVE_INFINITY : halfW / Math.abs(dx);
    const stepY = dy === 0 ? Number.POSITIVE_INFINITY : halfH / Math.abs(dy);
    // Die naeher liegende Kante gewinnt. Ein kleiner Abstand laesst die
    // Linie nicht am Rahmen kleben.
    const t = Math.min(stepX, stepY);
    const gap = 3;
    const length = Math.hypot(dx, dy);
    const out = Math.min(1, t + gap / Math.max(1, length));
    return { x: cx + dx * out, y: cy + dy * out };
  }

  function completeLink(toId: string): void {
    const fromId = linkFrom;
    linkFrom = null;
    element.classList.remove('board--linking');
    if (!fromId || fromId === toId) return;
    const exists = board.edges.some(
      (e) => (e.from === fromId && e.to === toId) || (e.from === toId && e.to === fromId),
    );
    if (!exists) {
      board.edges.push({ id: `${fromId}->${toId}`, from: fromId, to: toId });
      renderEdges();
      changed();
    }
    select(toId);
  }

  /* ------------------------------------------------------------ Auswahl */

  function markSelection(): void {
    for (const [id, node] of nodes) node.classList.toggle('obj--selected', id === selectedId);
  }

  function select(id: string | null): void {
    selectedId = id;
    if (selectedEdgeId) {
      selectedEdgeId = null;
      renderEdges();
      opts.onEdgeSelect?.(null);
    }
    markSelection();
    cull();
    opts.onSelect?.(find(id));
  }

  /* ------------------------------------------------- Ziehen und Groesse */

  function startDrag(ev: PointerEvent, object: CanvasObject, mode: 'move' | Handle): void {
    if (drawsWith(ev)) return;
    ev.preventDefault();
    ev.stopPropagation();
    // Im Verbindungsmodus zaehlt jeder Griff als Ziel – sonst wuerde ein
    // Tipper auf die Griffleiste das Verbinden stillschweigend abbrechen.
    if (linkFrom) {
      completeLink(object.id);
      return;
    }
    select(object.id);

    const node = nodes.get(object.id);
    const startX = ev.clientX;
    const startY = ev.clientY;
    const origin = { x: object.x, y: object.y, w: object.w, h: object.h };
    const ratio = origin.w / Math.max(1, origin.h);
    const pointerId = ev.pointerId;
    try {
      (ev.currentTarget as HTMLElement).setPointerCapture(pointerId);
    } catch {
      /* ohne Festhalten weiter */
    }
    // Waehrend des Ziehens keine Uebergaenge: die sollen beim Antippen
    // wirken, nicht dem Finger hinterherlaufen.
    node?.classList.add('obj--dragging');

    const onMove = (move: PointerEvent) => {
      if (move.pointerId !== pointerId) return;
      // Durch den Zoom teilen: eine Fingerbewegung von 10 Bildschirmpixeln
      // ist bei doppelter Vergroesserung nur 5 Weltpixel.
      const dx = (move.clientX - startX) / scale;
      const dy = (move.clientY - startY) / scale;

      if (mode === 'move') {
        object.x = Math.max(0, Math.min(WORLD_WIDTH - object.w, origin.x + dx));
        object.y = Math.max(0, origin.y + dy);
      } else {
        resize(object, mode, origin, ratio, dx, dy, move.shiftKey);
      }
      if (node) placeNode(object, node);
      renderEdges();
    };

    const onUp = (up: PointerEvent) => {
      if (up.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      node?.classList.remove('obj--dragging');
      growIfNeeded();
      changed();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /**
   * Groesse aendern, je nachdem an welchem Griff gezogen wird.
   *
   * Ecken aendern Breite und Hoehe, Kanten nur eine Richtung. Bild und Ton
   * behalten an der Ecke ihr Seitenverhaeltnis, denn ein verzerrtes Foto
   * will niemand; mit gedrueckter Umschalttaste gilt das fuer alle.
   *
   * Die Mindestgroesse wird beim Ziehen an Oberkante oder linker Kante
   * gegengerechnet, sonst wuerde das Objekt beim Anschlag wegrutschen.
   */
  function resize(
    object: CanvasObject,
    handle: Handle,
    origin: { x: number; y: number; w: number; h: number },
    ratio: number,
    dx: number,
    dy: number,
    forceRatio: boolean,
  ): void {
    const west = handle.includes('w');
    const east = handle.includes('e');
    const north = handle.includes('n');
    const south = handle.includes('s');

    let w = origin.w + (east ? dx : 0) - (west ? dx : 0);
    let h = origin.h + (south ? dy : 0) - (north ? dy : 0);

    const corner = (west || east) && (north || south);
    if (corner && (forceRatio || KEEP_RATIO.has(object.type))) {
      // Die groessere der beiden Aenderungen fuehrt, die andere folgt.
      if (Math.abs(w - origin.w) > Math.abs(h - origin.h)) h = w / ratio;
      else w = h * ratio;
    }

    w = Math.max(MIN_W, w);
    h = Math.max(MIN_H, h);
    // Nicht ueber den linken Rand der Welt hinaus.
    if (west) w = Math.min(w, origin.x + origin.w);
    if (north) h = Math.min(h, origin.y + origin.h);
    if (east) w = Math.min(w, WORLD_WIDTH - origin.x);

    object.w = w;
    object.h = h;
    object.x = west ? origin.x + origin.w - w : origin.x;
    object.y = north ? origin.y + origin.h - h : origin.y;
  }

  /* --------------------------------------------- Verschieben und Zoomen */

  /** Aktive Finger, fuer das Zoomen mit zwei Fingern. */
  const touches = new Map<number, { x: number; y: number }>();
  let panStart: { x: number; y: number; panX: number; panY: number } | null = null;
  let pinchStart: { distance: number; scale: number; cx: number; cy: number } | null = null;

  function onPointerDown(ev: PointerEvent): void {
    // Der Stift zeichnet, sobald ein Werkzeug an ist – dann nicht verschieben.
    if (drawsWith(ev)) return;
    if (linkFrom) {
      // Danebengetippt: Verbinden abbrechen.
      cancelLink();
      return;
    }
    touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (touches.size === 1) {
      selectEdge(null);
      select(null);
      panStart = { x: ev.clientX, y: ev.clientY, panX, panY };
      element.classList.add('board--panning');
    } else if (touches.size === 2) {
      panStart = null;
      const [a, b] = [...touches.values()] as [{ x: number; y: number }, { x: number; y: number }];
      pinchStart = {
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
      };
    }
  }

  function onPointerMove(ev: PointerEvent): void {
    if (!touches.has(ev.pointerId)) return;
    touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (pinchStart && touches.size >= 2) {
      const [a, b] = [...touches.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomAt(clampScale(pinchStart.scale * (distance / pinchStart.distance)), pinchStart.cx, pinchStart.cy);
      return;
    }

    if (panStart) {
      panX = panStart.panX + (ev.clientX - panStart.x);
      panY = panStart.panY + (ev.clientY - panStart.y);
      clampPan();
      applyTransform();
    }
  }

  function onPointerUp(ev: PointerEvent): void {
    touches.delete(ev.pointerId);
    if (touches.size < 2) pinchStart = null;
    if (touches.size === 0) {
      panStart = null;
      element.classList.remove('board--panning');
      growIfNeeded();
      changed();
    }
  }

  /** Zoomt so, dass der Punkt unter den Fingern stehen bleibt. */
  function zoomAt(next: number, clientX: number, clientY: number): void {
    const rect = element.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const worldX = (px - panX) / scale;
    const worldY = (py - panY) / scale;
    scale = next;
    panX = px - worldX * scale;
    panY = py - worldY * scale;
    clampPan();
    applyTransform();
    changed();
  }

  /** Haelt die Welt im Bild: man kann sich nicht ins Nichts verschieben. */
  function clampPan(): void {
    const viewW = element.clientWidth;
    const viewH = element.clientHeight;
    const worldW = WORLD_WIDTH * scale;
    const worldH = board.worldHeight * scale;
    const slack = Math.min(viewW, viewH) * 0.5;
    panX = Math.min(slack, Math.max(viewW - worldW - slack, panX));
    panY = Math.min(slack, Math.max(viewH - worldH - slack, panY));
  }

  function onWheel(ev: WheelEvent): void {
    // Trackpad und Maus: mit Strg (bzw. Pinch) zoomen, sonst verschieben.
    if (ev.ctrlKey || ev.metaKey) {
      ev.preventDefault();
      zoomAt(clampScale(scale * (1 - ev.deltaY / 400)), ev.clientX, ev.clientY);
      return;
    }
    ev.preventDefault();
    panX -= ev.deltaX;
    panY -= ev.deltaY;
    clampPan();
    applyTransform();
    changed();
  }

  element.addEventListener('pointerdown', onPointerDown);
  element.addEventListener('pointermove', onPointerMove);
  element.addEventListener('pointerup', onPointerUp);
  element.addEventListener('pointercancel', onPointerUp);
  element.addEventListener('wheel', onWheel, { passive: false });

  /*
   * Die Flaeche darf niemals scrollen.
   *
   * Wo "overflow: clip" noch nicht greift, bleibt sie ein Scroll-Container:
   * Der Browser schiebt dann beim Fokussieren eines Textfelds das Objekt ins
   * Bild und verschiebt die Welt gegenueber ihrem Koordinatensystem. Danach
   * saessen Verschieben, Zoomen und die Handschrift daneben. Also sofort
   * zuruecksetzen – die Bewegung machen wir selbst ueber die transform.
   */
  function onScroll(): void {
    if (element.scrollTop !== 0) element.scrollTop = 0;
    if (element.scrollLeft !== 0) element.scrollLeft = 0;
  }
  element.addEventListener('scroll', onScroll);

  const observer = new ResizeObserver(() => {
    clampPan();
    applyTransform();
  });
  observer.observe(element);

  /* ------------------------------------------------------------ Aufbau */

  applySize();
  applyBackground();
  renderObjects();
  renderEdges();
  requestAnimationFrame(() => {
    clampPan();
    applyTransform();
  });

  return {
    element,
    drawing: { host: element, content: world, getUnit: () => scale },

    addObject(partial) {
      // In der Mitte des sichtbaren Ausschnitts absetzen – und, falls dort
      // schon etwas liegt, treppenfoermig daneben. Sonst stapeln sich
      // mehrere neue Objekte unsichtbar uebereinander.
      const rect = element.getBoundingClientRect();
      const centre = toWorld(rect.left + element.clientWidth / 2, rect.top + element.clientHeight / 2);
      const object = emptyObject(partial);
      const spot = freeSpot(
        Math.max(0, Math.round(centre.x - object.w / 2)),
        Math.max(0, Math.round(centre.y - object.h / 2)),
        object.w,
        object.h,
      );
      object.x = spot.x;
      object.y = spot.y;
      board.objects.push(object);
      const node = objectNode(object);
      nodes.set(object.id, node);
      world.append(node);
      growIfNeeded();
      select(object.id);
      changed();
      // Direkt hineinschreiben koennen.
      requestAnimationFrame(() => {
        node.querySelector<HTMLTextAreaElement>('.obj__text')?.focus();
        node.querySelector<HTMLInputElement>('.obj__line')?.focus();
      });
      return object;
    },

    selected: () => find(selectedId),

    selectedEdge: () => board.edges.find((e) => e.id === selectedEdgeId) ?? null,

    deleteSelected() {
      // Eine gewaehlte Verbindung geht zuerst – sonst koennte man sie nie
      // loeschen, ohne auch das Objekt zu verlieren.
      if (selectedEdgeId) {
        board.edges = board.edges.filter((e) => e.id !== selectedEdgeId);
        selectedEdgeId = null;
        renderEdges();
        opts.onEdgeSelect?.(null);
        changed();
        return;
      }
      const id = selectedId;
      if (!id) return;
      board.objects = board.objects.filter((o) => o.id !== id);
      board.edges = board.edges.filter((e) => e.from !== id && e.to !== id);
      // Eine laufende Aufnahme muss mit dem Baustein verstummen.
      audios.get(id)?.pause();
      audios.delete(id);
      nodes.get(id)?.remove();
      nodes.delete(id);
      renderEdges();
      select(null);
      changed();
    },

    updateSelected(patch) {
      // Auf eine gewaehlte Verbindung wirkt nur die Farbe.
      if (selectedEdgeId) {
        const edge = board.edges.find((e) => e.id === selectedEdgeId);
        if (edge && patch.stroke !== undefined) {
          edge.color = patch.stroke;
          renderEdges();
          changed();
        }
        return;
      }
      const object = find(selectedId);
      const node = object ? nodes.get(object.id) : null;
      if (!object || !node) return;

      Object.assign(object, patch);

      // Farben und Schriftgroesse brauchen keinen Neuaufbau; die Form oder
      // eine neue Datei schon, weil sich das Innere aendert.
      paintColors(object, node);
      if (patch.shape !== undefined || patch.src !== undefined) {
        paintBody(object, node.querySelector('.obj__body') as HTMLElement);
      } else {
        for (const el of node.querySelectorAll<HTMLElement>('.obj__text, .obj__line')) {
          applyTextStyle(object, el);
        }
      }
      changed();
    },

    setBackground(kind) {
      board.background = kind;
      applyBackground();
      changed();
    },

    background: () => board.background ?? 'dots',

    startLink() {
      if (!selectedId) return;
      linkFrom = selectedId;
      element.classList.add('board--linking');
    },
    linking: () => linkFrom !== null,
    cancelLink,

    zoomBy(factor) {
      const rect = element.getBoundingClientRect();
      zoomAt(clampScale(scale * factor), rect.left + rect.width / 2, rect.top + rect.height / 2);
    },

    fit() {
      if (board.objects.length === 0) {
        scale = 1;
        panX = 0;
        panY = 0;
      } else {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        for (const o of board.objects) {
          minX = Math.min(minX, o.x);
          minY = Math.min(minY, o.y);
          maxX = Math.max(maxX, o.x + o.w);
          maxY = Math.max(maxY, o.y + o.h);
        }
        const pad = 80;
        const viewW = element.clientWidth || 1;
        const viewH = element.clientHeight || 1;
        scale = clampScale(
          Math.min(viewW / (maxX - minX + pad * 2), viewH / (maxY - minY + pad * 2)),
        );
        panX = viewW / 2 - ((minX + maxX) / 2) * scale;
        panY = viewH / 2 - ((minY + maxY) / 2) * scale;
      }
      clampPan();
      applyTransform();
      changed();
    },

    destroy() {
      clearTimeout(changeTimer);
      // Nichts soll weiterspielen, nachdem die Flaeche zu ist.
      for (const el of audios.values()) {
        el.pause();
        el.src = '';
      }
      audios.clear();
      // Letzten Stand nicht verlieren, wenn man schnell zurueckgeht.
      board.view = { x: -panX / scale, y: -panY / scale, scale };
      opts.onChange({
        ...board,
        objects: board.objects.map((o) => ({ ...o })),
        edges: board.edges.map((e) => ({ ...e })),
      });
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerUp);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('scroll', onScroll);
      observer.disconnect();
    },
  };

  function cancelLink(): void {
    linkFrom = null;
    element.classList.remove('board--linking');
  }

  /**
   * Sucht einen Platz, an dem das neue Objekt kein vorhandenes verdeckt.
   * Geht treppenfoermig nach rechts unten, wie neue Fenster auf dem
   * Schreibtisch. Nach ein paar Versuchen wird es hingelegt, wie es kommt –
   * lieber leicht versetzt als gar nicht.
   */
  function freeSpot(
    x: number,
    y: number,
    width: number,
    height: number,
  ): { x: number; y: number } {
    const STEP = 40;
    for (let i = 0; i < 30; i += 1) {
      const px = Math.max(0, Math.min(WORLD_WIDTH - width, x + i * STEP));
      const py = Math.max(0, y + i * STEP);
      // Echte Ueberdeckung pruefen, nicht nur den Abstand der Ecken: sonst
      // liegt eine grosse Karte trotzdem auf einer anderen.
      const taken = board.objects.some(
        (o) => px < o.x + o.w && px + width > o.x && py < o.y + o.h && py + height > o.y,
      );
      if (!taken) return { x: px, y: py };
    }
    return { x, y };
  }
}

/** Fuer Tests und die Liste: wie viele Punkte einer Checkliste sind erledigt? */
export function checklistProgress(text: string): { done: number; total: number } {
  const lines = text.split('\n').filter((l) => l.trim() !== '');
  return {
    done: lines.filter((l) => /^\[x\]/i.test(l.trim())).length,
    total: lines.length,
  };
}

/** Entfernt Verbindungen, deren Objekte es nicht mehr gibt. */
export function pruneEdges(objects: CanvasObject[], edges: CanvasEdge[]): CanvasEdge[] {
  const ids = new Set(objects.map((o) => o.id));
  return edges.filter((e) => ids.has(e.from) && ids.has(e.to));
}
